import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { useAppContext, useAppStore } from '@/hooks'
import { useSelectedBranchNumericId } from '@/pages/User/pages/Home/components/TreePanel/TreePanelContext'
import {
	useCreateDeploymentPackageMutation,
	useGetBranchesQuery,
	useGetUsersForComboUsingGetQuery,
} from '@/rtkApi'
import {
	getApiErrorDescription,
	logErrorAndNotifyWithToast,
} from '@/utils/errorHandling'

import { forSelectedSystem } from './systemScope'
import { DEPLOYMENT_PACKAGE_PAGE_SIZE } from './types'

type CreatePackageDialogProps = {
	onClose: () => void
	onCreated: (packageId: string) => void
	open: boolean
	systemId?: number
}

export const CreatePackageDialog = ({
	onClose,
	onCreated,
	open,
	systemId,
}: CreatePackageDialogProps) => {
	const { t } = useAppContext()
	const selectedBranchId = useSelectedBranchNumericId()
	const currentUserId = useAppStore((state) => state.auth.user?.id)
	const systemName = useAppStore((state) =>
		systemId != null ? state.node.nodes[systemId]?.name || state.node.nodes[systemId]?.code || '' : '',
	)
	const [name, setName] = useState('')
	const [description, setDescription] = useState('')
	const [branchId, setBranchId] = useState<number | ''>('')
	const [responsibleUserId, setResponsibleUserId] = useState<number | ''>('')
	const [createPackage, { isLoading }] = useCreateDeploymentPackageMutation()

	const { data: branches } = useGetBranchesQuery(
		{
			pageNumber: 1,
			pageSize: DEPLOYMENT_PACKAGE_PAGE_SIZE,
			status: ['ACTIVE', 'LOCKED'],
			systemId: systemId as number,
		},
		{ skip: !open || systemId == null },
	)
	const options = (
		systemId == null ? [] : forSelectedSystem(branches?.content ?? [], systemId)
	).filter((branch) => branch.id != null)
	const { data: users = [] } = useGetUsersForComboUsingGetQuery(undefined, { skip: !open })
	const userOptions = [...users].sort((left, right) =>
		left.value.localeCompare(right.value, undefined, { sensitivity: 'base' }),
	)
	const firstBranchId = options[0]?.id
	const selectedBranchIsListed = options.some((branch) => branch.id === selectedBranchId)

	useEffect(() => {
		if (!open) return
		setBranchId((current) => {
			if (current !== '') return current
			if (selectedBranchIsListed && selectedBranchId) return selectedBranchId
			return firstBranchId ?? ''
		})
	}, [firstBranchId, open, selectedBranchId, selectedBranchIsListed])

	useEffect(() => {
		if (!open || currentUserId == null) return
		if (!users.some((user) => user.id === currentUserId)) return
		setResponsibleUserId((current) => current || currentUserId)
	}, [currentUserId, open, users])

	const close = () => {
		setName('')
		setDescription('')
		setBranchId('')
		setResponsibleUserId('')
		onClose()
	}

	const submit = async () => {
		if (!name.trim() || systemId == null || branchId === '') return
		try {
			const created = await createPackage({
				releaseDto: {
					branch: { id: branchId },
					description: description.trim() || undefined,
					name: name.trim(),
					responsibleUser:
						responsibleUserId === '' ? undefined : { id: responsibleUserId },
				},
				systemId,
			}).unwrap()
			toast(t('DEPLOYMENT_PACKAGES_CREATE_SUCCESS'), { type: 'success' })
			close()
			if (created.id != null) onCreated(String(created.id))
		} catch (error) {
			logErrorAndNotifyWithToast({
				context: { operation: 'CreatePackageDialog.submit' },
				detail: getApiErrorDescription(error),
				error,
				messageKey: 'DEPLOYMENT_PACKAGES_CREATE_FAILED',
				t,
			})
		}
	}

	return (
		<Dialog
			open={open}
			onClose={close}
			maxWidth="xs"
			fullWidth
			slotProps={{ paper: { sx: { borderRadius: '14px' } } }}
		>
			<DialogTitle sx={{ color: '#1F4954', fontSize: 16, fontWeight: 700 }}>
				{t('DEPLOYMENT_PACKAGES_NEW')}
			</DialogTitle>
			<DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
				{systemName ? (
					<TextField
						size="small"
						fullWidth
						label={t('SYSTEM')}
						value={systemName}
						disabled
						sx={{ mt: 1 }}
					/>
				) : null}
				<TextField
					size="small"
					fullWidth
					label={t('DEPLOYMENT_PACKAGES_NAME')}
					value={name}
					autoFocus
					onChange={(event) => setName(event.target.value)}
					data-testid="deployment-package-name"
					sx={{ mt: systemName ? 0 : 1 }}
				/>
				<FormControl size="small" fullWidth>
					<InputLabel>{t('BRANCHES_SOURCE_BRANCH')}</InputLabel>
					<Select
						label={t('BRANCHES_SOURCE_BRANCH')}
						value={branchId}
						onChange={(event) => setBranchId(Number(event.target.value))}
						data-testid="deployment-package-branch"
					>
						{options.map((branch) => (
							<MenuItem key={branch.id} value={branch.id}>
								{branch.name}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<FormControl size="small" fullWidth>
					<InputLabel>{t('DEPLOYMENT_PACKAGES_COL_OWNER')}</InputLabel>
					<Select
						label={t('DEPLOYMENT_PACKAGES_COL_OWNER')}
						value={responsibleUserId}
						onChange={(event) => setResponsibleUserId(Number(event.target.value))}
						data-testid="deployment-package-responsible-user"
					>
						{userOptions.map((user) => (
							<MenuItem key={user.id} value={user.id}>
								{user.value}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				{options.length === 0 ? (
					<Typography sx={{ color: '#C26401', fontSize: 12 }}>
						{t('DEPLOYMENT_PACKAGES_NO_BRANCH')}
					</Typography>
				) : null}
				<TextField
					size="small"
					fullWidth
					label={t('DESCRIPTION')}
					value={description}
					multiline
					minRows={2}
					onChange={(event) => setDescription(event.target.value)}
				/>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button onClick={close} sx={{ textTransform: 'none' }}>
					{t('CANCEL')}
				</Button>
				<Button
					variant="contained"
					disableElevation
					disabled={!name.trim() || branchId === '' || isLoading || systemId == null}
					onClick={() => void submit()}
					data-testid="deployment-package-submit"
					sx={{ textTransform: 'none' }}
				>
					{t('CREATE')}
				</Button>
			</DialogActions>
		</Dialog>
	)
}
